import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  approveAiCheckpoint,
  getAiCheckpointPuckData,
  getAiCheckpointSections,
  getAiCheckpointUnmappedFields,
  revertAiCheckpoint,
  type AiCheckpointDetail,
  type PuckComponent,
} from "@/hooks/use-page-ai";
import { useStreamingGenerate } from "@/hooks/use-creative-api";
import {
  buildSectionReviewInstruction,
  getSectionApprovalSummary,
  getSectionReviewKey,
  getSectionReviewSummary,
  getSectionReviewTitle,
  type SectionReviewChoice,
} from "./section-review";

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function buildDraftTitle(pageType: string) {
  const normalized = pageType.trim();
  if (!normalized) {
    return "AI Draft";
  }
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)} Draft`;
}

function buildEditorContinuationHref(pageId: string, entry: "generated" | "template" = "generated") {
  const search = new URLSearchParams({
    mode: "simple",
    entry,
  });

  return `/pages/editor/${pageId}?${search.toString()}`;
}

function formatDraftStatus(status: AiCheckpointDetail["status"]) {
  if (status === "pending") {
    return "Draft ready";
  }
  if (status === "approved") {
    return "Saved";
  }
  if (status === "rejected") {
    return "Discarded";
  }
  return "Expired";
}

export default function StreamingGenerator() {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkpoint, loading, error, generate, refine, reject, refresh } = useStreamingGenerate();
  const [prompt, setPrompt] = useState("");
  const [pageType, setPageType] = useState("lead_capture");
  const [refinementNotes, setRefinementNotes] = useState("");
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [surfaceError, setSurfaceError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState("Ready to create the first draft.");
  const [displayVersion, setDisplayVersion] = useState<number | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [sectionDecisions, setSectionDecisions] = useState<Record<string, SectionReviewChoice>>({});
  const [sectionFeedback, setSectionFeedback] = useState<Record<string, string>>({});
  const [activeFeedbackKey, setActiveFeedbackKey] = useState<string | null>(null);
  const [sectionActionKey, setSectionActionKey] = useState<string | null>(null);
  const [visibleSectionCount, setVisibleSectionCount] = useState(0);
  const hasAppliedSeedState = useRef(false);

  const seededImportState = location.state as
    | {
      prompt?: string;
      pageType?: string;
      title?: string;
      slug?: string;
    }
    | null;

  const visibleCheckpoint = useMemo(() => {
    if (!checkpoint) {
      return null;
    }

    const versionNumber = displayVersion ?? checkpoint.currentVersion;
    const version = checkpoint.versions.find((entry) => entry.versionNumber === versionNumber) ?? null;
    return version ? { checkpoint, version } : null;
  }, [checkpoint, displayVersion]);

  const sections = useMemo(() => {
    if (!visibleCheckpoint) {
      return [];
    }

    return getAiCheckpointSections({
      ...visibleCheckpoint.checkpoint,
      currentVersion: visibleCheckpoint.version.versionNumber,
    } as AiCheckpointDetail);
  }, [visibleCheckpoint]);

  const unmappedFields = useMemo(() => {
    if (!visibleCheckpoint) {
      return [];
    }

    return getAiCheckpointUnmappedFields({
      ...visibleCheckpoint.checkpoint,
      currentVersion: visibleCheckpoint.version.versionNumber,
    } as AiCheckpointDetail);
  }, [visibleCheckpoint]);

  const isViewingLatestDraft = Boolean(
    checkpoint
      && visibleCheckpoint
      && visibleCheckpoint.version.versionNumber === checkpoint.currentVersion,
  );

  const sectionReviewKeys = useMemo(() => {
    if (!visibleCheckpoint) {
      return [];
    }

    return sections.map((component, index) =>
      getSectionReviewKey(visibleCheckpoint.version.versionNumber, component, index));
  }, [sections, visibleCheckpoint]);

  const sectionApproval = useMemo(() => {
    return getSectionApprovalSummary(sectionReviewKeys, sectionDecisions);
  }, [sectionDecisions, sectionReviewKeys]);
  const visibleSections = useMemo(
    () => sections.slice(0, visibleSectionCount),
    [sections, visibleSectionCount],
  );
  const isRevealingSections = Boolean(
    checkpoint && sections.length > 0 && visibleSectionCount < sections.length,
  );

  useEffect(() => {
    if (!checkpoint) {
      return;
    }

    setDisplayVersion(checkpoint.currentVersion);
  }, [checkpoint]);

  useEffect(() => {
    if (!checkpoint || title.trim()) {
      return;
    }

    const nextTitle = buildDraftTitle(pageType);
    setTitle(nextTitle);
    setSlug(slugify(nextTitle));
  }, [checkpoint, pageType, title]);

  useEffect(() => {
    setActiveFeedbackKey(null);
    setSectionActionKey(null);
  }, [displayVersion, checkpoint?.currentVersion]);

  useEffect(() => {
    setSectionDecisions({});
    setSectionFeedback({});
    setActiveFeedbackKey(null);
    setSectionActionKey(null);
  }, [checkpoint?.id]);

  useEffect(() => {
    if (!checkpoint || loading || sections.length === 0) {
      setVisibleSectionCount(0);
      return;
    }

    setVisibleSectionCount(0);

    let nextCount = 0;
    const intervalId = window.setInterval(() => {
      nextCount += 1;
      setVisibleSectionCount(Math.min(nextCount, sections.length));

      if (nextCount >= sections.length) {
        window.clearInterval(intervalId);
      }
    }, 280);

    return () => window.clearInterval(intervalId);
  }, [checkpoint?.id, displayVersion, loading, sections.length]);

  useEffect(() => {
    if (hasAppliedSeedState.current || !seededImportState) {
      return;
    }

    if (seededImportState.prompt?.trim()) {
      setPrompt(seededImportState.prompt.trim());
    }
    if (seededImportState.pageType?.trim()) {
      setPageType(seededImportState.pageType.trim());
    }
    if (seededImportState.title?.trim()) {
      setTitle(seededImportState.title.trim());
    }
    if (seededImportState.slug?.trim()) {
      setSlug(seededImportState.slug.trim());
    }

    setStatusMessage("Import context loaded. Review the seeded prompt, then generate the first draft.");
    hasAppliedSeedState.current = true;
  }, [seededImportState]);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      return;
    }

    setSurfaceError(null);
    setStatusMessage("Creating the first draft…");
    const detail = await generate(prompt.trim(), { pageType });

    if (!detail) {
      setSurfaceError("Failed to create the draft.");
      return;
    }

    setStatusMessage("Draft ready. Review each section, request changes if needed, then save it to the editor.");
  }, [generate, pageType, prompt]);

  const handleRefine = useCallback(async () => {
    if (!refinementNotes.trim()) {
      return;
    }

    setSurfaceError(null);
    setStatusMessage("Creating a new draft version…");
    const detail = await refine(refinementNotes.trim());

    if (!detail) {
      setSurfaceError("Failed to update the draft.");
      return;
    }

    setRefinementNotes("");
    setStatusMessage(`Draft updated. You are now reviewing version ${detail.currentVersion}.`);
  }, [refine, refinementNotes]);

  const handleRestore = useCallback(async (versionNumber: number) => {
    if (!checkpoint) {
      return;
    }

    setIsRestoring(true);
    setSurfaceError(null);

    try {
      await revertAiCheckpoint(checkpoint.id, versionNumber);
      const refreshed = await refresh();
      if (!refreshed) {
        throw new Error("Failed to reload the draft after restore.");
      }
      setStatusMessage(`You switched back to draft version ${versionNumber}.`);
    } catch (restoreError) {
      setSurfaceError(restoreError instanceof Error ? restoreError.message : "Failed to restore that draft version.");
    } finally {
      setIsRestoring(false);
    }
  }, [checkpoint, refresh]);

  const handleSectionDecision = useCallback((reviewKey: string, decision: SectionReviewChoice) => {
    setSectionDecisions((current) => ({ ...current, [reviewKey]: decision }));
    if (decision !== "change") {
      setActiveFeedbackKey((current) => (current === reviewKey ? null : current));
    }
  }, []);

  const handleSectionChange = useCallback(async (
    component: PuckComponent,
    index: number,
    reviewKey: string,
  ) => {
    if (!isViewingLatestDraft) {
      return;
    }

    try {
      const instruction = buildSectionReviewInstruction({
        component,
        index,
        mode: "change",
        feedback: sectionFeedback[reviewKey],
      });

      setSectionActionKey(reviewKey);
      setSurfaceError(null);
      setStatusMessage(`Updating section ${index + 1} in a new draft version…`);
      const detail = await refine(instruction);

      if (!detail) {
        throw new Error("Failed to update this section.");
      }

      setActiveFeedbackKey(null);
      setStatusMessage(`Section ${index + 1} was updated in draft version ${detail.currentVersion}.`);
    } catch (sectionError) {
      setSurfaceError(sectionError instanceof Error ? sectionError.message : "Failed to update this section.");
    } finally {
      setSectionActionKey(null);
    }
  }, [isViewingLatestDraft, refine, sectionFeedback]);

  const handleSectionRedo = useCallback(async (
    component: PuckComponent,
    index: number,
    reviewKey: string,
  ) => {
    if (!isViewingLatestDraft) {
      return;
    }

    try {
      const instruction = buildSectionReviewInstruction({
        component,
        index,
        mode: "redo",
      });

      setSectionActionKey(reviewKey);
      setSurfaceError(null);
      setStatusMessage(`Creating a fresh take for section ${index + 1}…`);
      const detail = await refine(instruction);

      if (!detail) {
        throw new Error("Failed to redo this section.");
      }

      setActiveFeedbackKey(null);
      setStatusMessage(`Section ${index + 1} was rebuilt in draft version ${detail.currentVersion}.`);
    } catch (sectionError) {
      setSurfaceError(sectionError instanceof Error ? sectionError.message : "Failed to redo this section.");
    } finally {
      setSectionActionKey(null);
    }
  }, [isViewingLatestDraft, refine]);

  const handleApprove = useCallback(async () => {
    if (!checkpoint || !title.trim() || !slug.trim()) {
      return;
    }

    setIsApproving(true);
    setSurfaceError(null);

    try {
      const approved = await approveAiCheckpoint(checkpoint.id, {
        title: title.trim(),
        slug: slug.trim(),
        pageType,
      });
      navigate(buildEditorContinuationHref(approved.page.id));
    } catch (approveError) {
      setSurfaceError(approveError instanceof Error ? approveError.message : "Failed to save the draft.");
    } finally {
      setIsApproving(false);
    }
  }, [checkpoint, navigate, pageType, slug, title]);

  const handleReject = useCallback(async () => {
    if (!checkpoint) {
      return;
    }

    setIsRejecting(true);
    setSurfaceError(null);

    try {
      await reject();
      setStatusMessage("Draft discarded. Nothing was saved.");
    } catch (rejectError) {
      setSurfaceError(rejectError instanceof Error ? rejectError.message : "Failed to discard the draft.");
    } finally {
      setIsRejecting(false);
    }
  }, [checkpoint, reject]);

  const visiblePuckData = visibleCheckpoint
    ? getAiCheckpointPuckData({
      ...visibleCheckpoint.checkpoint,
      currentVersion: visibleCheckpoint.version.versionNumber,
    } as AiCheckpointDetail)
    : null;

  return (
    <div className="flex h-full">
      <div className="w-96 shrink-0 border-r border-border bg-background p-4">
        <div className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Draft Builder</h2>
            <p className="text-sm text-muted-foreground">
              Create a real draft, review each section in plain language, then save it to the editor.
            </p>
          </div>

          <label className="space-y-2 text-sm">
            <span className="font-medium text-foreground">Page type</span>
            <select
              value={pageType}
              onChange={(event) => setPageType(event.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2"
              disabled={Boolean(checkpoint) || loading}
            >
              <option value="lead_capture">Lead capture</option>
              <option value="site_page">Site page</option>
              <option value="sales">Sales</option>
              <option value="booking">Booking</option>
              <option value="webinar">Webinar</option>
              <option value="thank_you">Thank you</option>
            </select>
          </label>

          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Describe the page you want to generate..."
            className="min-h-[120px] w-full rounded-md border border-border bg-background p-3 text-sm"
            disabled={loading}
          />

          <button
            onClick={handleGenerate}
            disabled={!prompt.trim() || loading}
            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading && !checkpoint ? "Generating…" : "Generate First Draft"}
          </button>

          <textarea
            value={refinementNotes}
            onChange={(event) => setRefinementNotes(event.target.value)}
            placeholder="Describe a whole-page change if you want a new full draft version..."
            className="min-h-[90px] w-full rounded-md border border-border bg-background p-3 text-sm"
            disabled={!checkpoint || loading}
          />

          <button
            onClick={handleRefine}
            disabled={!checkpoint || !refinementNotes.trim() || loading}
            className="w-full rounded-md border border-border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading && Boolean(checkpoint) ? "Updating…" : "Update Whole Draft"}
          </button>

          <div className="grid gap-3">
            <label className="space-y-2 text-sm">
              <span className="font-medium text-foreground">Draft title</span>
              <input
                value={title}
                onChange={(event) => {
                  const nextTitle = event.target.value;
                  setTitle(nextTitle);
                  setSlug((current) => current || slugify(nextTitle));
                }}
                placeholder="AI Draft"
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              />
            </label>

            <label className="space-y-2 text-sm">
              <span className="font-medium text-foreground">Slug</span>
              <input
                value={slug}
                onChange={(event) => setSlug(slugify(event.target.value))}
                placeholder="ai-draft"
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              />
            </label>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleReject}
              disabled={!checkpoint || isRejecting || isApproving || loading}
              className="flex-1 rounded-md border border-border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isRejecting ? "Discarding…" : "Discard Draft"}
            </button>
            <button
              onClick={handleApprove}
              disabled={!checkpoint || !title.trim() || !slug.trim() || !isViewingLatestDraft || !sectionApproval.allApproved || isApproving || isRejecting || loading}
              className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isApproving ? "Saving…" : "Save and Open Editor"}
            </button>
          </div>

          {(surfaceError || error) ? (
            <p className="text-sm text-[var(--color-destructive)]">{surfaceError ?? error}</p>
          ) : null}
          <p className="text-xs text-muted-foreground">{statusMessage}</p>
          {checkpoint ? (
            <p className="text-xs text-muted-foreground">
              {sectionApproval.totalCount === 0
                ? "Review the draft below before saving it."
                : `${sectionApproval.approvedCount} of ${sectionApproval.totalCount} sections marked “Looks good”.`}
            </p>
          ) : null}
          {isRevealingSections ? (
            <p className="text-xs text-muted-foreground">
              Streaming section {visibleSectionCount + 1} of {sections.length} into the review panel.
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col bg-muted p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-foreground">Draft Review</h3>
            <p className="text-xs text-muted-foreground">
              {visibleCheckpoint
                ? `Viewing draft version ${visibleCheckpoint.version.versionNumber}`
                : "No draft loaded yet"}
            </p>
          </div>
          {checkpoint?.versions.length ? (
            <div className="flex gap-2 overflow-x-auto">
              {checkpoint.versions.map((version) => (
                <button
                  key={version.versionNumber}
                  onClick={() => setDisplayVersion(version.versionNumber)}
                  disabled={loading || isRestoring}
                  className={`rounded border px-3 py-1 text-xs ${
                    (displayVersion ?? checkpoint.currentVersion) === version.versionNumber
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background text-muted-foreground"
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  Draft {version.versionNumber}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {checkpoint && !isViewingLatestDraft && visibleCheckpoint ? (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3 text-sm text-muted-foreground">
            <span>You are viewing an earlier draft. Restore it before making changes or saving.</span>
            <button
              onClick={() => handleRestore(visibleCheckpoint.version.versionNumber)}
              disabled={isRestoring || loading}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isRestoring ? "Restoring…" : "Use This Draft"}
            </button>
          </div>
        ) : null}

        {!checkpoint ? (
          <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border bg-background text-muted-foreground">
            Create a draft to start reviewing real builder output.
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-4">
              {visibleSections.map((component, index) => {
                const props = component.props ?? {};
                const reviewKey = visibleCheckpoint
                  ? getSectionReviewKey(visibleCheckpoint.version.versionNumber, component, index)
                  : `${component.type}-${index}`;
                const decision = sectionDecisions[reviewKey];
                const isUpdatingSection = sectionActionKey === reviewKey;
                const showFeedback = activeFeedbackKey === reviewKey;

                return (
                  <div key={`${component.type}-${index}`} className="rounded-lg border border-border bg-background p-6">
                    <div className="mb-4 flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Section {index + 1}</p>
                        <h4 className="mt-1 text-lg font-semibold text-foreground">{getSectionReviewTitle(component, index)}</h4>
                        <p className="mt-1 text-sm text-muted-foreground">{getSectionReviewSummary(component)}</p>
                      </div>
                      <span className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground">
                        {decision === "approved"
                          ? "Looks good"
                          : decision === "change"
                            ? "Needs changes"
                            : decision === "redo"
                              ? "Redo requested"
                              : "Not reviewed"}
                      </span>
                    </div>

                    {typeof props.heading === "string" ? <h2 className="mb-2 text-2xl font-bold text-foreground">{props.heading}</h2> : null}
                    {typeof props.subheading === "string" ? <p className="mb-2 text-lg text-muted-foreground">{props.subheading}</p> : null}
                    {typeof props.body === "string" ? <p className="text-sm text-foreground">{props.body}</p> : null}
                    {typeof props.buttonText === "string" ? (
                      <button className="mt-3 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">
                        {props.buttonText}
                      </button>
                    ) : null}
                    {!props.heading && !props.subheading && !props.body && !props.buttonText ? (
                      <pre className="overflow-x-auto rounded bg-muted p-3 text-xs text-muted-foreground">
                        {JSON.stringify(props, null, 2)}
                      </pre>
                    ) : null}

                    <div className="mt-4 space-y-3 border-t border-border pt-4">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleSectionDecision(reviewKey, "approved")}
                          disabled={!isViewingLatestDraft || loading || isApproving || isRejecting}
                          className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Looks good
                        </button>
                        <button
                          onClick={() => {
                            handleSectionDecision(reviewKey, "change");
                            setActiveFeedbackKey(reviewKey);
                          }}
                          disabled={!isViewingLatestDraft || loading || isApproving || isRejecting}
                          className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Change something
                        </button>
                        <button
                          onClick={() => handleSectionRedo(component, index, reviewKey)}
                          disabled={!isViewingLatestDraft || loading || isUpdatingSection || isApproving || isRejecting}
                          className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isUpdatingSection ? "Redoing…" : "Redo"}
                        </button>
                      </div>

                      {showFeedback ? (
                        <div className="space-y-2">
                          <textarea
                            value={sectionFeedback[reviewKey] ?? ""}
                            onChange={(event) => setSectionFeedback((current) => ({
                              ...current,
                              [reviewKey]: event.target.value,
                            }))}
                            placeholder="Describe what you want changed in this section."
                            className="min-h-[88px] w-full rounded-md border border-border bg-background p-3 text-sm"
                            disabled={loading || isUpdatingSection}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSectionChange(component, index, reviewKey)}
                              disabled={!sectionFeedback[reviewKey]?.trim() || loading || isUpdatingSection}
                              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {isUpdatingSection ? "Saving…" : "Update This Section"}
                            </button>
                            <button
                              onClick={() => setActiveFeedbackKey(null)}
                              disabled={loading || isUpdatingSection}
                              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              {isRevealingSections ? (
                <div className="rounded-lg border border-dashed border-border bg-background p-6 text-sm text-muted-foreground">
                  Section {visibleSectionCount + 1} is arriving next...
                </div>
              ) : null}
            </div>

            <aside className="space-y-4 rounded-lg border border-border bg-background p-4">
              <div>
                <h4 className="text-sm font-semibold text-foreground">Draft Summary</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Status: <span className="font-medium text-foreground">{formatDraftStatus(checkpoint.status)}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Latest version: <span className="font-medium text-foreground">{checkpoint.currentVersion}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Visible sections: <span className="font-medium text-foreground">{sections.length}</span>
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-foreground">Builder Data</h4>
                <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted p-3 text-xs text-muted-foreground">
                  {JSON.stringify(visiblePuckData, null, 2)}
                </pre>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-foreground">Review Notes</h4>
                {unmappedFields.length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">No builder mapping gaps were reported for this draft.</p>
                ) : (
                  <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
                    {unmappedFields.map((field) => (
                      <li key={`${field.componentId}:${field.fieldName}`} className="rounded bg-muted px-3 py-2">
                        <span className="font-medium text-foreground">{field.componentId}</span>
                        {` → ${field.fieldName}`}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
